import { Grid, makeStyles, Theme } from "@material-ui/core"
import React, { ReactChild, useEffect, useRef, useState } from "react"
import usePlatform from "../../hooks/usePlatform"
import { Locale } from "../../locale"

interface Props {
  slides: ReactChild[]
  prevElement?: ReactChild
  nextElement?: ReactChild
  lgSlidePerView: number
  smSlidePerView: number
  interval?: number
  showExtraSlides?: boolean
  touchEnabled?: boolean
}

interface StyleProps {
  slideWidth: number
  slideCount: number
  slideIdx: number
  slideTransition: boolean
  lgSlidePerView: number
  smSlidePerView: number
  interval: number
  touchDiff: number
  showExtraSlides: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
  },
  navigationButton: {
    backgroundColor: "transparent",
    outline: "none",
    border: "none",
    cursor: "pointer",
    height: "fit-content",
    zIndex: 10,
  },
  slideList: {
    maxWidth: ({ slideWidth, smSlidePerView }) => slideWidth * smSlidePerView,
    overflow: ({ showExtraSlides }) => (showExtraSlides ? "" : "hidden"),
    padding: "12px",
    [theme.breakpoints.up("md")]: {
      maxWidth: ({ slideWidth, lgSlidePerView }) => slideWidth * lgSlidePerView,
    },
  },
  slideTrack: {
    width: ({ slideWidth, slideCount }) => slideWidth * slideCount * 4,
    transform: ({ slideWidth, slideIdx, touchDiff }) => `translateX(-${slideWidth * slideIdx + touchDiff}px)`,
    transition: ({ slideTransition, interval }) => (slideTransition ? `transform ${interval / 1000}s ease` : ""),
  },
}))

export default function Carousel({
  slides,
  prevElement,
  nextElement,
  lgSlidePerView,
  smSlidePerView,
  interval = 300,
  showExtraSlides = false,
  touchEnabled = false,
}: Props) {
  const extendedSlides = [...slides, ...slides, ...slides, ...slides]
  const firstSlide = extendedSlides.shift()
  const [slideWidth, setSlideWidth] = useState(0)
  const [slideIdx, setSlideIdx] = useState(slides.length * 2)
  const [slideEnabled, setSlideEnabled] = useState(true)
  const [slideTransition, setSlideTransition] = useState(false)
  const [touchPosition, setTouchPosition] = useState<number | null>(null)
  const [touchDiff, setTouchDiff] = useState(0)
  const slideRef = useRef<HTMLDivElement>(null)
  const { lang } = Locale.getDefault()
  const { isMobile } = usePlatform()
  const classes = useStyles({
    slideWidth,
    slideCount: slides.length,
    slideIdx,
    slideTransition,
    lgSlidePerView,
    smSlidePerView,
    interval,
    touchDiff,
    showExtraSlides,
  })

  const showNavigation = slides.length > (isMobile ? smSlidePerView : lgSlidePerView)

  const slide = (idx: number) => {
    setSlideIdx(idx)
    setSlideEnabled(false)
    setTimeout(() => setSlideEnabled(true), interval)
  }

  const handlePrevButtonClick = () => {
    slide(slideIdx - 1)
    setSlideTransition(true)
  }

  const handleNextButtonClick = () => {
    slide(slideIdx + 1)
    setSlideTransition(true)
  }

  const handleSlideTouchStart = (position: number) => {
    if (!touchEnabled || !slideEnabled) return

    setTouchPosition(position)
    setSlideTransition(false)
  }

  const handleSlideTouchMove = (position: number) => {
    if (!touchPosition) return

    setTouchDiff(touchPosition - position)
  }

  const handleSlideTouchEnd = () => {
    const diff = slideWidth / 4

    if (touchDiff > diff) slide(slideIdx + 1)
    if (touchDiff < -diff) slide(slideIdx - 1)

    setTouchPosition(null)
    setTouchDiff(0)
    setSlideTransition(true)
  }

  useEffect(() => {
    const calculateSlideWidth = () => {
      if (!slideRef.current) return

      setSlideWidth(slideRef.current.clientWidth)
    }

    calculateSlideWidth()

    window.addEventListener("resize", calculateSlideWidth)

    return () => window.removeEventListener("resize", calculateSlideWidth)
  }, [slideRef])

  useEffect(() => {
    if (slides.length < slideIdx && slideIdx < slides.length * 3) return

    const resetSlides = () => {
      setSlideIdx(slides.length * 2)
      setSlideTransition(false)
    }

    const timerId = setTimeout(resetSlides, interval + 50)

    return () => clearTimeout(timerId)
  }, [slideIdx])

  return (
    <div className={classes.root}>
      {showNavigation && prevElement && (
        <button
          type="button"
          className={classes.navigationButton}
          onClick={handlePrevButtonClick}
          disabled={!slideEnabled}
          aria-label={lang === "ko" ? "이전 슬라이드" : "previous slide"}>
          {prevElement}
        </button>
      )}
      <div className={classes.slideList}>
        <Grid
          container
          spacing={3}
          className={classes.slideTrack}
          onTouchStart={(e) => handleSlideTouchStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleSlideTouchMove(e.touches[0].clientX)}
          onTouchEnd={handleSlideTouchEnd}
          onMouseDown={(e) => handleSlideTouchStart(e.clientX)}
          onMouseMove={(e) => handleSlideTouchMove(e.clientX)}
          onMouseUp={handleSlideTouchEnd}>
          <Grid item ref={slideRef}>
            {firstSlide}
          </Grid>
          {extendedSlides.map((slide, idx) => (
            <Grid item key={idx}>
              {slide}
            </Grid>
          ))}
        </Grid>
      </div>
      {showNavigation && nextElement && (
        <button
          type="button"
          className={classes.navigationButton}
          onClick={handleNextButtonClick}
          disabled={!slideEnabled}
          aria-label={lang === "ko" ? "다음 슬라이드" : "next slide"}>
          {nextElement}
        </button>
      )}
    </div>
  )
}
